import React, { forwardRef, useEffect, useMemo } from "react";
import { Box, Group, Input, Select, Text, TextInput } from "@mantine/core";
import { useTranslation } from "react-i18next";
import {
  ensurePhoneField,
  isPhoneObject,
  normalizePhone,
} from "../helpers/phoneUtils";
import {
  DATA_PHONE_COUNTRY_CODES,
  filterPhoneCountryCode,
  isoToFlagEmoji,
} from "../data/phoneCountryCodes";

const PhoneCountrySelectItem = forwardRef(
  ({ label, country, countryZh, iso, ...others }, ref) => (
    <div ref={ref} {...others}>
      <Group spacing="xs" noWrap sx={{ width: "100%" }}>
        <Text component="span" aria-hidden>
          {iso ? isoToFlagEmoji(iso) : "🌐"}
        </Text>
        <Text size="sm" sx={{ flex: 1 }}>
          {country}
          {countryZh ? ` ${countryZh}` : ""}
        </Text>
        <Text size="sm" color="dimmed">
          {label}
        </Text>
      </Group>
    </div>
  )
);

const PhoneInput = ({
  form,
  name,
  label,
  required = false,
  disabled = false,
  placeholder = "61210001",
  ...props
}) => {
  const { t } = useTranslation();

  const rawPhone = form?.values?.[name];
  const phone = normalizePhone(rawPhone);
  const canUseNestedProps = isPhoneObject(rawPhone);

  useEffect(() => {
    if (!form || !name) return;
    ensurePhoneField(form, name);
  }, [form, name, rawPhone]);

  const getSubFieldProps = (subField) => {
    const path = `${name}.${subField}`;
    if (canUseNestedProps) {
      return form.getInputProps(path);
    }

    return {
      value: phone[subField],
      onChange: (event) => {
        const nextValue =
          typeof event === "object" && event?.currentTarget
            ? event.currentTarget.value
            : event ?? "";
        form.setFieldValue(name, {
          ...normalizePhone(form.values?.[name]),
          [subField]: nextValue,
        });
      },
      error: form.errors?.[path],
    };
  };

  const countryProps = getSubFieldProps("countryCode");
  const areaProps = getSubFieldProps("areaCode");
  const numberProps = getSubFieldProps("number");

  const countryCodeData = useMemo(() => {
    const current = countryProps.value;
    if (
      !current ||
      DATA_PHONE_COUNTRY_CODES.some((item) => item.value === current)
    ) {
      return DATA_PHONE_COUNTRY_CODES;
    }
    return [
      { value: current, label: `+${current}` },
      ...DATA_PHONE_COUNTRY_CODES,
    ];
  }, [countryProps.value]);

  return (
    <Input.Wrapper
      label={label ? t(label) : undefined}
      required={required}
      withAsterisk={required}
      {...props}
    >
      <Group
        spacing="xs"
        align="flex-end"
        mt={label ? 4 : 0}
        noWrap
        sx={{ width: "100%" }}
      >
        <Select
          disabled={disabled}
          aria-label={t("Country Code")}
          placeholder="+852"
          searchable
          clearable={false}
          nothingFound={t("No options")}
          data={countryCodeData}
          filter={filterPhoneCountryCode}
          itemComponent={PhoneCountrySelectItem}
          withinPortal
          zIndex={1000}
          w={90}
          styles={{ dropdown: { minWidth: 280 } }}
          {...countryProps}
        />
        <TextInput
          disabled={disabled}
          aria-label={t("Area Code")}
          placeholder={t("Area Code")}
          w={90}
          {...areaProps}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <TextInput
            disabled={disabled}
            aria-label={t("Phone Number")}
            placeholder={placeholder}
            required={required}
            sx={{ width: "100%" }}
            {...numberProps}
          />
        </Box>
      </Group>
    </Input.Wrapper>
  );
};

export default PhoneInput;
